import { formatChildAgeBound } from "../childAgeBandUnits";

import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatMultiplier,
  formatPercent,
  formatMultipleDollarAmounts,
  formatMultipleSalaryMultipliers,
  formatDefinitionOfEarnings,
  enumLabel,
  formatAgeReductionSchedule,
  buildAgeReductionScheduleFormatter,
  PORTABILITY_LABELS,
  ANNUAL_OPEN_ENROLLMENT_LABELS,
  INCLUSION_LABELS,
  formatRateGuarantee,
} from "./shared";

export type VolLifeFieldFormatter = (row: Record<string, unknown>) => string;

/**
 * Benefit description - increments/dollarAmount/salaryMultiplier/multipleOptions/
 * percentageOfEmployeeElection/notIncluded. Single amount sibling, which holds a
 * `{amount}[]` list for the multi-value types.
 */
const benefitDescription =
  (
    typeKey: string,
    amountKey: string,
    flatDollar = false,
  ): VolLifeFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);

    if (type === "dollarAmount") {
      const amount = formatCurrency(row[amountKey]);
      if (!amount) return "";
      // EE/Spouse render "Flat $X"; child age bands render the bare amount.
      return flatDollar ? `Flat ${amount}` : amount;
    }
    if (type === "increments") {
      const amount = formatCurrency(row[amountKey]);
      return amount ? `${amount} increments` : "";
    }
    if (type === "salaryMultiplier")
      return formatMultiplier(row[amountKey]) || "";
    if (type === "percentageOfEmployeeElection")
      return formatPercent(row[amountKey])
        ? `${formatPercent(row[amountKey])} of EE Amount`
        : "";
    if (type === "multipleSalaryMultipliers")
      return formatMultipleSalaryMultipliers(row[amountKey]);
    if (type === "multipleOptions" || type === "multipleDollarAmounts")
      return formatMultipleDollarAmounts(row[amountKey]);
    return "";
  };

/**
 * Benefit maximum - dollarAmount/salaryMultiplier/lesserOfDollarAmountOrSalaryMultiplier.
 * Two amount siblings (DollarAmount, SalaryMultiplier) or one (Amount).
 */
const benefitMaximumTwo =
  (
    typeKey: string,
    dollarKey: string,
    multiplierKey: string,
  ): VolLifeFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "dollarAmount") return formatCurrency(row[dollarKey]) || "";
    if (type === "salaryMultiplier")
      return formatMultiplier(row[multiplierKey]) || "";
    if (type === "lesserOfDollarAmountOrSalaryMultiplier") {
      const dollar = formatCurrency(row[dollarKey]);
      const mult = formatMultiplier(row[multiplierKey]);
      if (dollar && mult) return `Lesser of ${dollar} or ${mult}`;
      return dollar || mult || "";
    }
    return "";
  };

const benefitMaximumOne =
  (typeKey: string, amountKey: string): VolLifeFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);

    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    return "";
  };

interface AgeBasedAmountBand {
  amount: number;
  // Carriers quote open-ended bands ("to age 69", "70 and over"), so a band
  // legitimately carries only one bound.
  ageLower?: number;
  ageUpper?: number;
}

// Blank strings must be treated as absent, not coerced: `Number(" ")` is 0,
// which would render an unquoted bound as age 0.
const toFiniteNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string" && value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// The age-based amounts sibling arrives as an array of `{amount, ageLower,
// ageUpper}` bands or, from some persisted shapes, a JSON-encoded string.
const parseAgeBasedAmounts = (raw: unknown): AgeBasedAmountBand[] => {
  let list: unknown = raw;
  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => {
      const { amount, ageLower, ageUpper } = (entry ?? {}) as {
        amount?: unknown;
        ageLower?: unknown;
        ageUpper?: unknown;
      };
      return {
        amount: Number(amount),
        ageLower: toFiniteNumber(ageLower),
        ageUpper: toFiniteNumber(ageUpper),
      };
    })
    .filter(
      (band) =>
        Number.isFinite(band.amount) &&
        (band.ageLower !== undefined || band.ageUpper !== undefined),
    );
};

// Per the requirements sheet, bands render one per line: the lowest as
// "<[upper+1]: $amount", the highest as "[lower]+: $amount", and any middle
// band as "[lower] - [upper]: $amount". Ex: "<70: $30,000" / "70+: $10,000".
const formatAgeBasedAmounts = (raw: unknown): string => {
  const bands = parseAgeBasedAmounts(raw)
    .slice()
    .sort(
      (a, b) =>
        (a.ageLower ?? a.ageUpper ?? 0) - (b.ageLower ?? b.ageUpper ?? 0),
    );

  return bands
    .map((band, index) => {
      const amount = formatCurrency(band.amount);
      const isFirst = index === 0;
      const isLast = index === bands.length - 1;

      let label: string;

      if (isFirst && band.ageUpper !== undefined) {
        label = `<${band.ageUpper + 1}`;
      } else if (isLast && band.ageLower !== undefined) {
        label = `${band.ageLower}+`;
      } else if (band.ageLower !== undefined && band.ageUpper !== undefined) {
        label = `${band.ageLower} - ${band.ageUpper}`;
      } else if (band.ageUpper !== undefined) {
        label = `<${band.ageUpper + 1}`;
      } else {
        label = `${band.ageLower}+`;
      }

      return `${label}: ${amount}`;
    })
    .join("\n");
};

export interface ChildBenefitBand {
  ageBandLower?: number;
  ageBandUpper?: number;
  unit?: string;
  benefitAmount?: {
    type?: string;
    amounts?: number[];
  };
}

// The child age-band array arrives as an array of bands or, from some persisted
// shapes, a JSON-encoded string (mirrors parseAgeBasedAmounts above).
export const parseChildBands = (raw: unknown): ChildBenefitBand[] => {
  let list: unknown = raw;

  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  return Array.isArray(list) ? (list as ChildBenefitBand[]) : [];
};

const formatChildBandAmount = (
  benefitAmount: ChildBenefitBand["benefitAmount"],
): string => {
  const type = benefitAmount?.type;
  if (isSentinel(type)) return sentinelLabel(type as string);

  const [first] = benefitAmount?.amounts ?? [];

  if (type === "dollarAmount") return formatCurrency(first) || "";
  if (type === "increments") {
    const amount = formatCurrency(first);
    return amount ? `${amount} increments` : "";
  }
  if (type === "percentageOfEmployeeElection") {
    const percent = formatPercent(first);
    return percent ? `${percent} of EE Amount` : "";
  }
  // The frontend invented `multipleDollarAmounts` for the editor's own type
  // field; the API's real enum value is `multipleOptions`. Bands written before
  // that mapping was fixed can still carry the invented value, so both are
  // accepted here.
  if (type === "multipleDollarAmounts" || type === "multipleOptions") {
    return formatMultipleDollarAmounts(
      (benefitAmount?.amounts ?? []).map((amount) => ({ amount })),
    );
  }

  return "";
};

/**
 * Child benefit description & ages - the flexible child benefit amounts (the
 * grid's `childBenefitAmountsByAge` row, v5 `benefitAmountsByAge`), CD-1976.
 * One line per band: "<lower> – <upper>: <amount>", with bounds in
 * their display units (e.g. "6 months – Age 2: $5,000"). The first band
 * starting at 0 reads "Birth to <upper>" - "Birth" rather than "0 days", joined
 * with "to" rather than the en dash, per the requirements sheet.
 */
export const formatChildAgeBandsValue = (bands: ChildBenefitBand[]): string =>
  bands
    .map((band, index) => {
      // "Birth" is a first band that genuinely starts at 0 - not one missing a
      // lower bound, which would be malformed data rather than a birth start.
      const startsAtBirth = index === 0 && band.ageBandLower === 0;
      const lower = startsAtBirth
        ? "Birth"
        : formatChildAgeBound(band.ageBandLower, band.unit);
      // The birth band joins with "to" ("Birth to 6 months"); every other band
      // keeps the en dash.
      const range = [lower, formatChildAgeBound(band.ageBandUpper, band.unit)]
        .filter(Boolean)
        .join(startsAtBirth ? " to " : " – ");
      const amount = formatChildBandAmount(band.benefitAmount);

      if (range && amount) return `${range}: ${amount}`;

      return range || amount;
    })
    .filter(Boolean)
    .join("\n");

const formatChildAgeBands: VolLifeFieldFormatter = (row) =>
  formatChildAgeBandsValue(parseChildBands(row["childBenefitAmountsByAge"]));

/**
 * Guarantee issue - shared type enum across EE/Spouse tiers.
 * Siblings: SalaryMultiplier, DollarAmount, AdditionalAmount, AgesDetails.
 */
const guaranteeIssue =
  (
    typeKey: string,
    multiplierKey: string,
    dollarKey: string,
    additionalKey: string,
    agesDetailsKey: string,
  ): VolLifeFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);

    if (type === "allGuaranteeIssue") return "All Amounts";
    if (type === "ageBasedAmounts")
      return formatAgeBasedAmounts(row[agesDetailsKey]);
    if (type === "dollarAmount") return formatCurrency(row[dollarKey]) || "";
    if (type === "salaryMultiplierWithCap") {
      const mult = formatMultiplier(row[multiplierKey]);
      const cap = formatCurrency(row[dollarKey]);
      if (mult && cap) return `${mult} up to ${cap}`;
      return mult || "";
    }
    if (type === "salaryMultiplierWithDollarAmountPlusAdditionalAmount") {
      const mult = formatMultiplier(row[multiplierKey]);
      const dollar = formatCurrency(row[dollarKey]);
      const extra = formatCurrency(row[additionalKey]);
      const base =
        mult && dollar ? `${mult} up to ${dollar}` : mult || dollar || "";
      return extra ? `${base} + ${extra}` : base;
    }
    return "";
  };

/** Amount not to exceed - percentageOfEmployeeElection/notApplicable/notIncluded. */
const amountNotToExceed =
  (typeKey: string, amountKey: string): VolLifeFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (type === "notApplicable") return "N/A";
    if (isSentinel(type)) return sentinelLabel(type as string);

    if (type === "percentageOfEmployeeElection")
      return formatPercent(row[amountKey])
        ? `${formatPercent(row[amountKey])} of EE Amount`
        : "";
    return "";
  };

/** Student limiting age - included → "Included" (age ignored); other values shown as-is. */
const formatStudentLimitingAge: VolLifeFieldFormatter = (row) => {
  const type = row["studentStatusStudentLimitingAgeType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "included") return "Included";
  return "";
};

const ADND_TIED_LABELS: Record<string, string> = {
  tied: "Yes",
  untied: "No",
  clientLevelDecision: "Client Level Decision",
};

export const volLifeFormatters: Record<string, VolLifeFieldFormatter> = {
  // Employee
  benefitDescriptionEmployee: benefitDescription(
    "benefitDescriptionEmployeeType",
    "benefitDescriptionEmployeeAmount",
    true,
  ),
  benefitMaximumEmployee: benefitMaximumTwo(
    "benefitMaximumEmployeeType",
    "benefitMaximumEmployeeDollarAmount",
    "benefitMaximumEmployeeSalaryMultiplier",
  ),
  guaranteeIssueEmployee: guaranteeIssue(
    "guaranteeIssueEmployeeType",
    "guaranteeIssueEmployeeSalaryMultiplier",
    "guaranteeIssueEmployeeDollarAmount",
    "guaranteeIssueEmployeeAdditionalAmount",
    "guaranteeIssueEmployeeAgesDetails",
  ),

  // Spouse
  benefitDescriptionSpouse: benefitDescription(
    "benefitDescriptionSpouseType",
    "benefitDescriptionSpouseAmount",
    true,
  ),
  benefitMaximumSpouse: benefitMaximumOne(
    "benefitMaximumSpouseType",
    "benefitMaximumSpouseAmount",
  ),
  guaranteeIssueSpouse: guaranteeIssue(
    "guaranteeIssueSpouseType",
    "guaranteeIssueSpouseSalaryMultiplier",
    "guaranteeIssueSpouseDollarAmount",
    "guaranteeIssueSpouseAdditionalAmount",
    "guaranteeIssueSpouseAgesDetails",
  ),
  amountNotToExceedSpouse: amountNotToExceed(
    "amountNotToExceedSpouseType",
    "amountNotToExceedSpouseAmount",
  ),

  // Child benefit description & ages - flexible band array (Present, CD-1976).
  childBenefitAmountsByAge: formatChildAgeBands,

  // Child age bands (Build's three fixed rows)
  benefitDescriptionChildDependentAgesBirthTo14Days: benefitDescription(
    "benefitDescriptionChildDependentAgesBirthTo14DaysType",
    "benefitDescriptionChildDependentAgesBirthTo14DaysAmount",
  ),
  benefitDescriptionChildDependentAges15DaysTo6Months: benefitDescription(
    "benefitDescriptionChildDependentAges15DaysTo6MonthsType",
    "benefitDescriptionChildDependentAges15DaysTo6MonthsAmount",
  ),
  benefitDescriptionChildDependentAges6MonthsToLimitingAge: benefitDescription(
    "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
    "benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount",
  ),
  benefitMaximumChild: benefitMaximumOne(
    "benefitMaximumChildType",
    "benefitMaximumChildAmount",
  ),
  amountNotToExceedChild: amountNotToExceed(
    "amountNotToExceedChildType",
    "amountNotToExceedChildAmount",
  ),
  studentStatusStudentLimitingAge: formatStudentLimitingAge,

  // Shared
  definitionOfEarnings: formatDefinitionOfEarnings,
  ageReductionSchedule: formatAgeReductionSchedule,
  // Two independent age-reduction fields (Employee, Spouse), unlike the
  // single generic ageReductionScheduleType/Schedules pair other LOCs use
  // (CD-2555).
  ageReductionsEe: buildAgeReductionScheduleFormatter(
    "ageReductionsEe",
    "employeeAgeReductionSchedules",
  ),
  ageReductionsSp: buildAgeReductionScheduleFormatter(
    "ageReductionsSp",
    "spouseAgeReductionSchedules",
  ),

  // Simple enums
  adndIncluded: enumLabel("adndIncluded", INCLUSION_LABELS),
  adndTiedToLifeElection: enumLabel("adndTiedToLifeElection", ADND_TIED_LABELS),
  portability: enumLabel("portability", PORTABILITY_LABELS),
  conversion: enumLabel("conversion", INCLUSION_LABELS),
  acceleratedDeathBenefit: enumLabel(
    "acceleratedDeathBenefit",
    INCLUSION_LABELS,
  ),
  waiverOfPremium: enumLabel("waiverOfPremium", INCLUSION_LABELS),
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,
};

export const formatVolLifeField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = volLifeFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
